import React from 'react';
import { connect } from 'react-redux';

import ErrorMessage from '@modules/common/components/banners/ErrorMessage';
import { PrimaryBlankLink } from '@src/components/common/links';
import { StoreState } from '@src/meta/types/store';
import {
  SentinelUser,
  Claims,
} from '@src/config/default/modules/Authentication/components/types/user';
import { ComplianceStatus } from '@src/meta/types/compliance';
import WarningMessage from '@modules/common/components/banners/WarningMessage';

const CONTACT_LINK = 'accountreview@planet.com';

type Props = {
  message: string;
};

type PropsFromStore = {
  user: SentinelUser | undefined;
};

function BaseComplianceError({ user, message }: PropsFromStore & Props) {
  if (!user) {
    return null;
  }
  if (user.complianceStatus === ComplianceStatus.MATCH) {
    return <ComplianceMatchMsg />;
  }
  if (user.complianceStatus === ComplianceStatus.POTENTIAL_MATCH) {
    return <CompliancePotentialMatchMsg message={message} userId={user.id} email={user.email} />;
  }
  return null;
}

const mapStateToProps = (state: StoreState<SentinelUser, Claims>): PropsFromStore => ({
  user: state.auth.configurableUser,
});

const ComplianceError = connect(mapStateToProps)(BaseComplianceError);

function ComplianceMatchMsg() {
  return (
    <ErrorMessage>
      Thank you for your interest in Planet. Unfortunately, we are unable to grant you access to our
      platform due to restrictions under applicable legal requirements. We appreciate your interest
      in our services.
    </ErrorMessage>
  );
}

function CompliancePotentialMatchMsg({
  message,
  userId,
  email,
}: Props & { userId: string; email: string }) {
  return (
    <WarningMessage error>
      {message} Please contact your Planet representative or send the below information to{' '}
      <PrimaryBlankLink href={`mailto:${CONTACT_LINK}`}>{CONTACT_LINK}</PrimaryBlankLink>:
      <ul>
        <li>
          Sentinel Hub User ID: <code>{userId}</code>
        </li>
        <li>
          Sentinel Hub account email: <code>{email}</code>
        </li>
        <li>Full legal name</li>
        <li>Full residential address (including country)</li>
        <li>Birthdate</li>
        <li>Organization legal name (if applicable)</li>
        <li>Organization headquarters full address including country (if applicable)</li>
      </ul>
      <div>
        This might be the same information you have provided in the past, but we need to revalidate
        the information.
      </div>
    </WarningMessage>
  );
}

export function BillingComplianceError() {
  return (
    <ComplianceError message="We need a bit more information in order to accept your payment." />
  );
}

export function UserDataComplianceError() {
  return <ComplianceError message="We need a bit more information about your account." />;
}
